/**
 * <article-editorial> — redistributes a blog post's rich-text content into
 * two columns at >=990px: text stays in .article-template__content, media
 * (images, video, iframe embeds) moves into .article__media-column,
 * mirroring the product page's text-left/media-right layout (see
 * assets/main-product-editorial.css and sections/main-product.liquid).
 *
 * article.content is one HTML blob — images, iframes and paragraphs
 * interleaved, edited freehand by the client in Shopify's rich text editor.
 * Liquid can't reliably split that apart (a naive split on '<img' works on
 * today's posts and breaks the moment one doesn't match), so the whole
 * thing renders once into the text column and this walks the result
 * client-side instead.
 *
 * A custom element, not a DOMContentLoaded script, for the same reason as
 * assets/editorial-grid-swap.js: connectedCallback re-runs cleanly if this
 * ever ends up on content injected later, and gives every instance on the
 * page its own independent state.
 */
if (!customElements.get('article-editorial')) {
  const MEDIA_TAGS = ['IMG', 'PICTURE', 'VIDEO', 'IFRAME'];
  // Three levels of wrapper (e.g. a <div> around a <p> around an <img>) is
  // already generous for rich-text output — this just stops a pathological
  // document from recursing indefinitely.
  const MAX_DEPTH = 3;

  class ArticleEditorial extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.content = this.querySelector('.article-template__content');
      this.mediaColumn = this.querySelector('.article__media-column');
      if (!this.content || !this.mediaColumn) return;

      this.initialized = true;
      this.distributed = false;

      // The authored order, captured once before anything ever moves — a
      // desktop-to-mobile-and-back resize restores exactly this, rather
      // than whatever order a second distribute() pass might produce.
      this.originalNodes = Array.from(this.content.childNodes);

      this.mediaQuery = window.matchMedia('(min-width: 990px)');
      this.onMediaChange = this.onMediaChange.bind(this);
      this.mediaQuery.addEventListener('change', this.onMediaChange);
      this.onMediaChange(this.mediaQuery);
    }

    disconnectedCallback() {
      if (this.mediaQuery) this.mediaQuery.removeEventListener('change', this.onMediaChange);
    }

    onMediaChange(mediaQueryList) {
      if (mediaQueryList.matches) {
        this.distribute();
      } else {
        this.restore();
      }
    }

    // A node with an id may be a target of aria-describedby (or any other
    // in-page reference) elsewhere on the page — leave it exactly where it
    // was authored rather than risk moving something that's pointed at.
    isMovable(node) {
      return !node.id;
    }

    hasMedia(node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      if (MEDIA_TAGS.includes(node.tagName)) return true;
      return !!node.querySelector(MEDIA_TAGS.join(', '));
    }

    // Classifies one node (element or text node) as:
    //   'media' — is, or contains only, media
    //   'text'  — carries real text and no media
    //   'mixed' — carries both
    //   'none'  — neither (whitespace-only text, an empty element, a
    //             comment) — ignored by the caller, same as 'text'
    //
    // Bottom-up: an element's kind comes from combining its own children's
    // kinds, so a wrapper several levels deep with nothing but media
    // anywhere inside it still resolves to 'media' as a whole.
    classify(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent.trim() === '' ? 'none' : 'text';
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return 'none';

      if (MEDIA_TAGS.includes(node.tagName)) return 'media';

      // A <figure>'s caption doesn't count against it — the whole point of
      // this case is moving the figure so the caption travels with its
      // image, not leaving it stranded because there was text inside.
      if (node.tagName === 'FIGURE') {
        return this.hasMedia(node) ? 'media' : 'text';
      }

      const kinds = Array.from(node.childNodes)
        .map((child) => this.classify(child))
        .filter((kind) => kind !== 'none');

      if (kinds.length === 0) return 'none';
      if (kinds.every((kind) => kind === 'media')) return 'media';
      if (kinds.every((kind) => kind === 'text')) return 'text';
      return 'mixed';
    }

    // A text NODE sitting directly inside `node` (not nested one level
    // further, inside some child element) means real text and media are
    // interleaved right here — an <img> beside it genuinely shares this
    // block and must not be pulled out from beside it. A 'mixed' node
    // without any text of its OWN, only text-bearing and media-only child
    // ELEMENTS side by side, is safe to open up: each child is already its
    // own self-contained block.
    hasOwnInlineText(node) {
      return Array.from(node.childNodes).some(
        (child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim() !== ''
      );
    }

    // Walks `container`'s direct children, moving whole media-classified
    // elements and descending into safely-openable mixed ones (see
    // hasOwnInlineText) to find media nested beside text one level down.
    distributeFrom(container, depth) {
      // Static snapshot before moving anything — the live children
      // collection would shift under us mid-loop as each match is
      // appended elsewhere.
      Array.from(container.children).forEach((node) => {
        if (!this.isMovable(node)) return;

        const kind = this.classify(node);
        if (kind === 'media') {
          // appendChild moves the existing node (no clone, no
          // re-serialisation, wrapper included — a responsive embed's
          // aspect-ratio wrapper survives intact) — recreating an <iframe>
          // would reload it and restart any embedded video.
          this.mediaColumn.appendChild(node);
        } else if (kind === 'mixed' && depth < MAX_DEPTH && !this.hasOwnInlineText(node)) {
          this.distributeFrom(node, depth + 1);
        }
      });
    }

    distribute() {
      if (this.distributed) return;
      this.distributed = true;

      this.distributeFrom(this.content, 0);
      this.updateEmptyState();
    }

    restore() {
      if (!this.distributed) return;
      this.distributed = false;

      this.originalNodes.forEach((node) => {
        this.content.appendChild(node);
      });

      this.classList.remove('article__layout--no-media');
    }

    updateEmptyState() {
      this.classList.toggle('article__layout--no-media', this.mediaColumn.children.length === 0);
    }
  }

  customElements.define('article-editorial', ArticleEditorial);
}
